import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProductCollectionSearchItem } from '@models/productCollection';
import { getUniqueKeyFromUUID } from '@utils/string';
import { Button, Input, Select } from 'antd';
import React, { useEffect, useState } from 'react';

interface SortableProductCollectionSearchItemsItemProps {
  onDeleteItem?: (event: any) => void;
  onChange?: (item: ProductCollectionSearchItem) => void;
  id: string;
  item: ProductCollectionSearchItem;
}

export const SortableProductCollectionSearchItemsItem: React.FC<SortableProductCollectionSearchItemsItemProps> = (
  props,
) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { onChange, item } = props;

  return (
    <div ref={setNodeRef} style={{ ...style }} {...attributes} {...listeners}>
      <div
        style={{
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #eaeaea',
        }}>
        <div style={{ display: 'flex' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>검색타입</th>
                  <th style={{ width: '100px' }}>판매방식</th>
                  <th style={{ width: '140px' }}>정렬</th>
                  <th style={{ width: '100px' }}>최대 목록 수</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ textAlign: 'center' }}>
                    <Select
                      style={{ width: '140px' }}
                      value={item.type}
                      onChange={(value) => {
                        onChange && onChange({ ...item, type: value });
                      }}
                      options={[
                        { value: 'keyword', label: '검색어' },
                        { value: 'coordinates', label: 'GPS 좌표' },
                      ]}></Select>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Select
                      style={{ width: '100px' }}
                      value={item.saleType}
                      onChange={(value) => {
                        onChange && onChange({ ...item, saleType: value });
                      }}
                      options={[
                        { value: 'all', label: '전체' },
                        { value: 'commerce', label: '커머스' },
                        { value: 'class', label: '클래스' },
                      ]}></Select>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Select
                      style={{ width: '140px' }}
                      value={item.sort}
                      onChange={(value) => {
                        onChange && onChange({ ...item, sort: value });
                      }}
                      options={[
                        { value: 'recent', label: '최신순' },
                        { value: 'price_desc', label: '높은 가격순' },
                        { value: 'price_asc', label: '낮은 가격순' },
                        { value: 'near_asc', label: '가까운 거리순' },
                      ]}></Select>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Input
                      style={{ width: '100px' }}
                      value={item.size || 100}
                      onChange={(e) => {
                        onChange && onChange({ ...item, size: Number(e.target.value) || 100 });
                      }}></Input>
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', width: '60px', flexShrink: 0 }}>버튼명</div>
              <Input
                style={{ width: 320 }}
                value={item.label}
                onChange={(e) => {
                  onChange && onChange({ ...item, label: e.target.value });
                }}></Input>
            </div>
            {item.type === 'keyword' && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', width: '60px', flexShrink: 0 }}>검색어</div>
                <Input
                  style={{ width: 320 }}
                  value={item.keyword}
                  onChange={(e) => {
                    onChange && onChange({ ...item, keyword: e.target.value });
                  }}></Input>
              </div>
            )}
            {item.type === 'coordinates' && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', width: '60px', flexShrink: 0 }}>GPS 좌표</div>
                <Input
                  style={{ width: 320 }}
                  placeholder="(예: 127.041756,37.496877)"
                  value={item.coordinates}
                  onChange={(e) => {
                    onChange && onChange({ ...item, coordinates: e.target.value });
                  }}></Input>
              </div>
            )}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Button
              style={{ color: 'red' }}
              onClick={(e) => {
                props.onDeleteItem?.(e);
              }}>
              삭제
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EditProductCollectionSearchItemsView: React.FC<{
  data: ProductCollectionSearchItem[];
  onChange?: (data: ProductCollectionSearchItem[]) => void;
}> = (props) => {
  const [changedContents, setChangedContents] = useState<ProductCollectionSearchItem[]>();
  const { data, onChange } = props;

  const contents = changedContents || data || [];

  const sortableSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  useEffect(() => {
    onChange?.(contents);
  }, [changedContents]);

  const onSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      if (contents) {
        const oldIndex = contents.findIndex((item) => item.id === active.id);
        const newIndex = contents.findIndex((item) => item.id === over?.id);
        const newData = arrayMove(contents, oldIndex, newIndex);
        setChangedContents(newData);
      }
    }
  };

  return (
    <div>
      <div
        style={{
          height: '32px',
          fontWeight: 'bold',
          fontSize: '16px',
          borderBottom: `2px solid #f52c97`,
          marginBottom: '8px',
        }}>
        {'기획전 검색 필터 수정'}
      </div>
      <div style={{ height: 'calc(100dvh - 320px)', overflow: 'auto' }}>
        <div>
          <div style={{ display: 'flex', height: '44px', justifyContent: 'space-between' }}>
            <Button
              onClick={() => {
                setChangedContents((prev) => {
                  const newValues = [
                    { id: getUniqueKeyFromUUID(), type: 'keyword', saleType: 'all' },
                    ...(prev || contents),
                  ];
                  return newValues as ProductCollectionSearchItem[];
                });
              }}>
              빈 항목 추가
            </Button>
          </div>
          <DndContext sensors={sortableSensors} collisionDetection={closestCenter} onDragEnd={onSortEnd}>
            <SortableContext items={contents} strategy={verticalListSortingStrategy}>
              <div style={{ maxWidth: '680px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {contents.map((item, index) => {
                  return (
                    <SortableProductCollectionSearchItemsItem
                      item={item}
                      id={item.id}
                      onDeleteItem={() => {
                        setChangedContents((prev) => {
                          const newValues = [...(prev || contents)];
                          newValues.splice(index, 1);
                          return newValues;
                        });
                      }}
                      onChange={(item) => {
                        setChangedContents((prev) => {
                          const newValues = [...(prev || contents)];
                          newValues[index] = item;
                          return newValues;
                        });
                      }}></SortableProductCollectionSearchItemsItem>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default EditProductCollectionSearchItemsView;
